import { AfterViewInit, Component, Input } from "@angular/core";
import { EMPTY, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { CryptoTransaction, FiatTransaction, TradableTransaction, Transaction } from "@models/transaction";
import { TradableWalletListService } from "../../services/wallet/tradable_wallet_list.service";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { PayoutProvider } from "@models";
import { FiatWalletService } from "../../services/wallet/fiat_wallet.service";
import { TradableWalletService } from "../../services/wallet/tradable_wallet.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Tradable } from "@models/interfaces/tradable";
import { FiatDepositModalComponent } from "../step-summary/fiat-transactions/fiat-deposit-modal/fiat-deposit-modal.component";
import { DepositPendingComponent } from "@modules/main/components/step-summary/success-screens/deposit-pending/deposit-pending.component";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { Paysafe3DSModalComponent } from "../../../paysafe/components/paysafe-3ds-modal/paysafe-3ds-modal.component";
import { SendEntryPointService } from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { PaymentOptionsFacade } from "@modules/+new/payments/features/payment-options/domain/+state/payment-options.facade";
import { PaymentOption } from "@modules/+new/payments/features/payment-options/domain/entities/payment-option.model";

@Component({
	selector: "actions-bar",
	templateUrl: "./actions-bar.component.html",
	styleUrls: ["./actions-bar.component.scss"],
	standalone: false
})
export class ActionsBarComponent implements AfterViewInit {
	@Input()
	public transaction: Transaction = null;

	@Input()
	public canOpen: boolean = true;

	private loading: boolean;
	private provider: PayoutProvider = null;

	public isSendFiat: boolean = false;
	public isPaymentOptionSupported$: Observable<boolean> = EMPTY;

	public constructor(
		private readonly tradableWalletListService: TradableWalletListService,
		private readonly modalService: ModalService,
		private readonly confirmModalService: ConfirmModalService,
		private readonly fiatWalletService: FiatWalletService,
		private readonly masterdataService: MasterdataService,
		private readonly sendEntryPoint: SendEntryPointService,
		private readonly paymentOptionsFacade: PaymentOptionsFacade
	) {}

	public ngAfterViewInit(): void {
		// Set flag
		if (this.transaction instanceof FiatTransaction) {
			this.provider = this.masterdataService.payout_providers.find(
				(p) => p.getId() === (this.transaction as FiatTransaction).getPayoutProviderId()
			);
			if (this.provider) {
				this.isSendFiat = this.provider.sendEnabled(true);
			}

			this.isPaymentOptionSupported$ = this.isPaymentOptionSupported(
				Number(this.transaction.getPaymentOptionId())
			);
		}
	}

	public hasAction(b: boolean = false): boolean {
		return this.transaction.hasAction(b);
	}

	public showActionsBar(b: boolean = false): boolean {
		return this.hasAction(b) || this.isProcessingAmazonTx();
	}

	// Amazon tx that have been confirmed but still need processing by putting in the sent code are returned as status "processing"
	public isProcessingAmazonTx(): boolean {
		return (
			!!this.provider &&
			this.provider.getShortName() === "amazon" &&
			this.transaction.getStatus() === "processing"
		);
	}

	public isWithdrawal(): boolean {
		return this.transaction.isWithdrawal();
	}

	public isTradableTx(): boolean {
		return this.transaction instanceof TradableTransaction;
	}

	public showResendEmailLink(): boolean {
		return this.isWithdrawal() && !this.isProcessingAmazonTx();
	}

	public resendEmail(): void {
		if (this.isTradableTx()) {
			// Get rid of this once fiat is included!!!
			this.walletService.resendEmail(<TradableTransaction>this.transaction);
		} else {
			this.fiatWalletService.resendEmail(<FiatTransaction>this.transaction);
		}
	}

	public resendAmazon(): void {
		this.fiatWalletService.resendAmazon(<FiatTransaction>this.transaction);
	}

	public resendSmsPin(): void {
		this.sendEntryPoint.openFiatSendModal(<FiatTransaction>this.transaction);
	}

	private get walletService(): TradableWalletService<TradableWallet, TradableTransaction, Tradable> {
		return this.tradableWalletListService.serviceForId(this.transaction.getWalletId());
	}

	public isConfirmable(): boolean {
		if (this.transaction instanceof FiatTransaction) {
			return this.transaction.isConfirmable();
		}
		return false;
	}

	public isPayable(): boolean {
		if (this.transaction instanceof FiatTransaction) {
			return this.transaction.isPayable();
		}
		return false;
	}

	private isPaymentOptionSupported(id: number): Observable<boolean> {
		return this.paymentOptionsFacade
			.getPaymentOptionById$(id)
			.pipe(map((option: PaymentOption) => option.enabled && !option.unavailable));
	}

	public showConfirmButtons(): boolean {
		return this.hasAction() && !this.isWithdrawal() && !this.isTradableTx();
	}

	public cancel(): void {
		if (this.loading) {
			return;
		}
		this.loading = true;
		const isCrypto = this.transaction instanceof CryptoTransaction;
		this.confirmModalService
			.showConfirmModal(
				"cancel_transaction_title",
				isCrypto ? "cancel_crypto_transaction" : "cancel_fiat_transaction",
				"cancel-transaction",
				"back"
			)
			.subscribe(
				(answer: boolean) => {
					if (answer) {
						if (this.transaction instanceof TradableTransaction) {
							this.walletService.cancel(this.transaction).then(
								() => {
									this.walletService.fetchWallets();
									this.loading = false;
								},
								() => (this.loading = false)
							);
						} else if (this.transaction instanceof FiatTransaction) {
							this.fiatWalletService.cancel(this.transaction).then(
								() => {
									this.fiatWalletService.initWallets(true);
									this.loading = false;
								},
								() => {
									this.loading = false;
								}
							);
						}
					} else {
						this.loading = false;
					}
				},
				() => (this.loading = false)
			);
	}

	public isCancelable(): boolean {
		return this.transaction.isCancelable();
	}

	public restoreDeposit(): void {
		this.modalService
			.create(
				FiatDepositModalComponent,
				{
					data: { transaction: this.transaction },
					created: true,
					summary: true,
					pinSent: true
				},
				{ showCloseButton: false }
			)
			.present();
	}

	public async payTransaction(): Promise<void> {
		if (await this.fiatWalletService.isWebHandled(this.transaction as FiatTransaction)) {
			this.modalService
				.create(Paysafe3DSModalComponent, { transaction: this.transaction }, { preventBackdropDismiss: true })
				.present();
		} else {
			if (this.transaction instanceof FiatTransaction) {
				const pendingModal = this.modalService.create(
					DepositPendingComponent,
					{ transaction: this.transaction },
					{ backdropDismiss: false }
				);
				pendingModal.present();
			} else {
				throw new Error("payTransaction is only supported on fiat transactions and credit card transactions");
			}
		}
	}
}
